import { useCallback, useEffect, useState } from 'react';
import { Title, usePermissions, useResourceDefinitions } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import GroupIcon from '@mui/icons-material/Group';
import KeyIcon from '@mui/icons-material/Key';
import { menuLabel } from '../menuConfig';
import { FeatureGate, useFeatureFlags } from '../featureFlags/FeatureGate';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  createRole,
  fetchCatalog,
  fetchHistory,
  fetchRoles,
  type Catalog,
  type RoleChange,
  type RoleView,
} from './roleCenterApi';
import { HistoryTable, RoleAvatar, RoleCard } from './RoleCard';
import { RoleFormDialog } from './RoleDialogs';
import { PermissionMatrix, VisibilityMatrix } from './RoleMatrix';
import { SeeAsPreview } from './SeeAsPreview';
import { ROLE_CENTER_FLAG } from './roleAccessApi';

/**
 * „Centrum ról i uprawnień” (decyzja użytkownika 22.10.2026: „centrum
 * zarządzania rolami i uprawnieniami ról […] kto co widzi na froncie,
 * dostęp tylko admin, ładnie z ikonkami, podfunkcje z opisami”; przełącznik
 * `roleCenter`, S-22.10-01).
 *
 * Lista ról jako karty z ikoną, opisem, liczbą kont i uprawnień; karta roli
 * z zakładkami; macierze rola × uprawnienie i rola × widoczność; podgląd
 * „Zobacz jako”; historia zmian. Tylko administrator — API odpowiada 403
 * każdemu innemu, a ekran od razu mówi to samo. Administrator ma zawsze
 * wszystko (zablokowane, z opisem), gość nie dostanie zmian danych, element
 * bez uprawnienia jest wyszarzony z wyjaśnieniem.
 */

type CenterTab = 'roles' | 'permissions' | 'visibility' | 'see-as' | 'history';

export const CENTER_TABS: Array<{ key: CenterTab; label: string }> = [
  { key: 'roles', label: 'Role' },
  { key: 'permissions', label: 'Macierz uprawnień' },
  { key: 'visibility', label: 'Macierz widoczności' },
  { key: 'see-as', label: 'Zobacz jako' },
  { key: 'history', label: 'Historia zmian' },
];

export const ADMIN_ONLY_NOTE = 'Centrum ról i uprawnień jest dostępne tylko dla administratora.';
export const FEATURE_OFF_NOTE =
  'Centrum ról i uprawnień jest wyłączone w wersji aplikacji, z której korzystasz — uprawnienia ról działają jak dotąd.';

export const SCREEN_NOTE =
  'Tu określasz, co może każda rola i co widzi w aplikacji. Każda zmiana wymaga powodu i trafia do historii oraz dziennika działań. Ukrycie elementu w interfejsie niczego nie odbiera — o dostępie rozstrzyga serwer według uprawnień.';

const RoleTile = ({ role, onOpen }: { role: RoleView; onOpen: () => void }) => (
  <Card variant="outlined" sx={{ height: '100%' }} data-testid={`role-card-${role.id}`}>
    <CardActionArea onClick={onOpen} sx={{ height: '100%', alignItems: 'flex-start', display: 'flex' }}>
      <CardContent sx={{ width: '100%' }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1 }}>
          <RoleAvatar role={role} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {role.name}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
              {role.system ? (
                <Chip size="small" label="systemowa" sx={{ height: 20 }} />
              ) : (
                <Chip size="small" color="primary" variant="outlined" label="własna" sx={{ height: 20 }} />
              )}
              {role.locked ? <Chip size="small" icon={<LockIcon />} label="stała" sx={{ height: 20 }} /> : null}
              {!role.active ? <Chip size="small" color="warning" label="nieaktywna" sx={{ height: 20 }} /> : null}
            </Stack>
          </Box>
        </Stack>
        {/* Opis skracany do trzech wierszy — pełna treść w dymku (audyt układu 23.10.2026). */}
        <Typography
          variant="body2"
          color="text.secondary"
          title={role.description ?? undefined}
          sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 60 }}
        >
          {role.description ?? 'Bez opisu.'}
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }} data-testid={`role-card-users-${role.id}`}>
            <GroupIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {role.userCount} {role.userCount === 1 ? 'konto' : 'kont'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }} data-testid={`role-card-permissions-${role.id}`}>
            <KeyIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {role.permissions.length} z {role.permissionCells.length} uprawnień
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </CardActionArea>
  </Card>
);

const CenterBody = () => {
  const definitions = useResourceDefinitions();
  const features = useFeatureFlags();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [roles, setRoles] = useState<RoleView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<CenterTab>('roles');
  const [selected, setSelected] = useState<string | null>(null);
  const [history, setHistory] = useState<RoleChange[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const reload = useCallback(() => {
    fetchRoles()
      .then(setRoles)
      .catch((e) => setError(toErrorMessage(e)));
  }, []);

  useEffect(() => {
    fetchCatalog()
      .then(setCatalog)
      .catch((e) => setError(toErrorMessage(e)));
    reload();
  }, [reload]);

  useEffect(() => {
    if (tab !== 'history') return;
    let alive = true;
    setHistory(null);
    fetchHistory()
      .then((h) => alive && setHistory(h))
      .catch(() => alive && setHistory([]));
    return () => {
      alive = false;
    };
  }, [tab]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!catalog || !roles) return <CircularProgress size={24} />;

  const open = (id: string) => {
    setSelected(id);
    setTab('roles');
  };

  if (selected) {
    return (
      <RoleCard
        roleId={selected}
        catalog={catalog}
        roles={roles}
        onBack={() => setSelected(null)}
        onChanged={reload}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info" data-testid="role-center-intro">
        {SCREEN_NOTE}
      </Alert>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1 }}>
        <Tabs value={tab} onChange={(_, v: CenterTab) => setTab(v)} variant="scrollable" data-testid="role-center-tabs">
          {CENTER_TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} data-testid={`role-center-tab-${t.key}`} />
          ))}
        </Tabs>
        {tab === 'roles' ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateError(null);
              setCreateOpen(true);
            }}
            data-testid="role-add"
          >
            Dodaj rolę
          </Button>
        ) : null}
      </Stack>

      {tab === 'roles' ? (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          }}
          data-testid="role-list"
        >
          {roles.map((r) => (
            <RoleTile key={r.id} role={r} onOpen={() => open(r.id)} />
          ))}
        </Box>
      ) : null}
      {tab === 'permissions' ? <PermissionMatrix catalog={catalog} roles={roles} onOpen={open} /> : null}
      {tab === 'visibility' ? <VisibilityMatrix catalog={catalog} roles={roles} onOpen={open} /> : null}
      {tab === 'see-as' ? (
        <SeeAsPreview
          catalog={catalog}
          roles={roles}
          registered={Object.keys(definitions)}
          flags={features?.flags ?? {}}
        />
      ) : null}
      {tab === 'history' ? <HistoryTable rows={history} testId="role-center-history" showRole /> : null}

      {createOpen ? (
        <RoleFormDialog
          open
          mode="create"
          roles={roles}
          icons={catalog.icons}
          saving={saving}
          error={createError}
          onClose={() => setCreateOpen(false)}
          onSave={async (values, reason) => {
            setSaving(true);
            setCreateError(null);
            try {
              const created = await createRole({
                name: values.name.trim(),
                description: values.description.trim() || null,
                icon: values.icon,
                copyFrom: values.copyFrom || null,
                reason,
              });
              setCreateOpen(false);
              reload();
              setSelected(created.id);
            } catch (e) {
              setCreateError(toErrorMessage(e));
            } finally {
              setSaving(false);
            }
          }}
        />
      ) : null}
    </Stack>
  );
};

export const RoleCenter = () => {
  const { permissions: role, isPending } = usePermissions<string>();
  return (
    <Box sx={{ p: 2 }} data-testid="role-center">
      <Title title={menuLabel('role-center')} />
      <FeatureGate
        feature={ROLE_CENTER_FLAG}
        loading={<CircularProgress size={24} />}
        fallback={<Alert severity="info">{FEATURE_OFF_NOTE}</Alert>}
      >
        {isPending ? (
          <CircularProgress size={24} />
        ) : role === 'ADMIN' ? (
          <CenterBody />
        ) : (
          <Alert severity="warning" data-testid="role-center-admin-only">
            {ADMIN_ONLY_NOTE}
          </Alert>
        )}
      </FeatureGate>
    </Box>
  );
};

export default RoleCenter;
